import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../store';
import { loadPeopleConfig } from '../../../store/thunks/peopleConfigThunks';
import AdminBreadcrumb from '../../Admin/components/AdminBreadcrumb';
import AdminButton from '../../Admin/components/AdminButton';
import { AdminColors } from '../../Admin/constants/adminColors';
import FontFamily from '../../../constants/fonts';
import ConfigTabBar from './components/ConfigTabBar';
import { HeaderActionContext } from './PeopleConfigContext';
import { LazyConfigTab } from './PeopleHubConfigLazyComponents';
import { ConfigTabId } from './configTypes';
import { DESKTOP_MIN_WIDTH, DESKTOP_ONLY_MESSAGE, TAB_SUBTITLES } from './constants';

export interface PeopleHubConfigurationProps {
  onBack?: () => void;
}

export const PeopleHubConfiguration: React.FC<PeopleHubConfigurationProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<ConfigTabId>('ramp');
  const [headerAction, setHeaderAction] = useState<React.ReactNode>(null);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch<AppDispatch>();

  // Single consolidated load (metadata + every tab) for the scope held in Redux.
  // Tab switches do not reload; scope changes and Retry dispatch this again.
  useEffect(() => {
    dispatch(loadPeopleConfig());
  }, [dispatch]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <AdminBreadcrumb
        items={[{ label: 'Configurations', onPress: onBack }, { label: 'People Hub Configuration' }]}
      />
      <View style={styles.pageHeader}>
        <View style={styles.headerText}>
          <Text style={styles.pageTitle}>People Hub Configuration</Text>
          <Text style={styles.pageSubtitle}>{TAB_SUBTITLES[activeTab]}</Text>
        </View>
        <View style={styles.headerActions}>
          <AdminButton label="Back to Configurations" variant="secondary" onPress={onBack} />
          {headerAction}
        </View>
      </View>

      {width < DESKTOP_MIN_WIDTH ? (
        <View style={styles.desktopOnly}>
          <Text style={styles.desktopOnlyText}>{DESKTOP_ONLY_MESSAGE}</Text>
        </View>
      ) : (
        <>
          <ConfigTabBar activeTab={activeTab} onChange={setActiveTab} />
          <View style={styles.tabsGap} />
          <HeaderActionContext.Provider value={setHeaderAction}>
            <LazyConfigTab tab={activeTab} />
          </HeaderActionContext.Provider>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  content: { paddingTop: 24, paddingHorizontal: 32, paddingBottom: 64 },
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    minHeight: 56,
    marginBottom: 8,
  },
  tabsGap: { height: 12 },
  headerText: { flex: 1, minWidth: 280 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    lineHeight: 40,
  },
  pageSubtitle: {
    fontSize: 14,
    color: AdminColors.textSecondary,
    lineHeight: 20,
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  desktopOnly: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 48,
    alignItems: 'center',
  },
  desktopOnlyText: {
    fontSize: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
});

export default PeopleHubConfiguration;
